(() => {
  if (customElements.get('hod-shop-order-form')) return;

  class HodShopOrderForm extends HTMLElement {
  #frame;
  #summary;
  #summaryText;
  #reviewButton;
  #frameInView = false;
  #caseCount = 0;
  #total = 0;
  #observer;
  #scrollFrame = 0;

  connectedCallback() {
    this.#frame = this.querySelector('[data-hod-shop-frame]');
    this.#summary = this.querySelector('[data-hod-shop-summary]');
    this.#summaryText = this.querySelector('[data-hod-shop-summary-text]');
    this.#reviewButton = this.querySelector('[data-hod-shop-review]');

    if (!this.#frame || !this.#summary || !this.#summaryText || !this.#reviewButton) return;

    window.addEventListener('message', this.#onMessage);
    window.addEventListener('resize', this.#renderSummary);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#reviewButton.addEventListener('click', this.#requestReview);

    this.#observer = new IntersectionObserver(this.#onIntersection, { threshold: 0.01 });
    this.#observer.observe(this.#frame);
  }

  disconnectedCallback() {
    window.removeEventListener('message', this.#onMessage);
    window.removeEventListener('resize', this.#renderSummary);
    window.removeEventListener('scroll', this.#onScroll);
    this.#reviewButton?.removeEventListener('click', this.#requestReview);
    this.#observer?.disconnect();
    this.#observer = undefined;
    window.cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = 0;
  }

  #expectedOrigin() {
    if (this.#frame?.srcdoc) return window.location.origin;

    try {
      return new URL(this.#frame?.src || this.dataset.iframeUrl || '', window.location.href).origin;
    } catch {
      return null;
    }
  }

  #onIntersection = (entries) => {
    const entry = entries[0];
    this.#frameInView = Boolean(entry?.isIntersecting);
    this.#renderSummary();
  };

  #onMessage = (event) => {
    if (!this.#frame || event.source !== this.#frame.contentWindow) return;
    if (event.origin !== this.#expectedOrigin()) return;

    const message = event.data;
    if (!message || typeof message !== 'object') return;

    if (message.type === 'pcp:resize' || Number.isFinite(message.height)) {
      this.#resizeFrame(message.height);
    }

    if (message.type === 'pcp:scroll-top' || message.scrollToTop === true) {
      this.#scrollToFrame();
    }

    if (message.type === 'pcp:order-summary') {
      this.#updateSummary(message);
    }

    if (message.type === 'pcp:scroll-to') {
      this.#scrollToChildOffset(message.top);
    }
  };

  #resizeFrame(height) {
    if (!Number.isFinite(height) || height <= 0) return;
    this.#frame.style.height = `${Math.min(Math.round(height), 10000)}px`;
  }

  #updateSummary(message) {
    if (!Number.isInteger(message.caseCount) || message.caseCount < 0 || message.caseCount > 1200) return;
    if (!Number.isFinite(message.total) || message.total < 0 || message.total > 1_000_000_000_000) return;
    this.#caseCount = message.caseCount;
    this.#total = message.total;
    this.#renderSummary();
  }

  #renderSummary = () => {
    const frameBottom = this.#frame?.getBoundingClientRect().bottom ?? 0;
    const summaryClearance = (this.#summary?.offsetHeight ?? 0) + 24;
    const isNearFrameEnd = frameBottom <= window.innerHeight + summaryClearance;
    const show = this.#caseCount > 0
      && this.#frameInView
      && !isNearFrameEnd
      && window.matchMedia('(max-width: 749px)').matches;
    this.#summary.hidden = !show;
    if (!show) return;

    const noun = this.#caseCount === 1 ? 'case' : 'cases';
    const total = new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(this.#total);
    this.#summaryText.textContent = `${this.#caseCount} ${noun} · ${total} incl. VAT`;
  }

  #onScroll = () => {
    if (this.#scrollFrame) return;
    this.#scrollFrame = window.requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      this.#renderSummary();
    });
  };

  #requestReview = () => {
    const origin = this.#expectedOrigin();
    if (!origin || !this.#frame?.contentWindow) return;
    this.#frame.contentWindow.postMessage({ type: 'pcp:review-order' }, origin);
  };

  #scrollToFrame() {
    const top = this.#documentTopFor(this.#frame) - this.#headerOffset();
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  #scrollToChildOffset(offset) {
    if (!Number.isFinite(offset) || offset < 0 || !this.#frame) return;
    const boundedOffset = Math.min(Math.round(offset), this.#frame.offsetHeight);
    const top = this.#documentTopFor(this.#frame) + boundedOffset - this.#headerOffset();
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  #documentTopFor(element) {
    return element.getBoundingClientRect().top + window.scrollY;
  }

  #headerOffset() {
    const headerHeight = Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height'));
    return Number.isFinite(headerHeight) ? headerHeight + 16 : 16;
  }
  }

  customElements.define('hod-shop-order-form', HodShopOrderForm);
})();
